import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Modal,
  IconButton,
  Divider,
  Button,
  InputAdornment,
  TextField,
  Chip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { formatUtcDateSimple } from '../../../helpers/formatUtcDate';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { subDays, addDays } from 'date-fns';
import { useDispatch } from 'react-redux';
import { getComplianceList } from '../../../store/compliance/complianceSlice';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import './ListScans.css';

// Custom input for DatePicker with calendar icon
const DatePickerInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
  <TextField
    size="small"
    value={value}
    onClick={onClick}
    ref={ref}
    placeholder={placeholder}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <CalendarMonthIcon sx={{ color: "#1976d2" }} />
        </InputAdornment>
      ),
      readOnly: true,
      sx: {
        backgroundColor: 'white',
        borderRadius: '4px',
        fontSize: '14px',
        width: '150px',
        cursor: 'pointer',
      }
    }}
    sx={{
      '& .MuiOutlinedInput-root': {
        paddingRight: 0,
      }
    }}
  />
));

const ListScans = ({ open, onClose, scans = [], selectedAccount, onScanSelect, availableDates = [], complianceResultOrderedMap = {}, nextRunDate }) => {
  const dispatch = useDispatch();
  
  // Find the latest available date (as a Date object)
  const getLatestAvailableDate = () => {
    if (availableDates.length) {
      return new Date(
        availableDates
          .map(d => new Date(d.replace(' ', 'T')))
          .sort((a, b) => b - a)[0]
      );
    }
    if (scans[0]?.scanDate) {
      return new Date(scans[0].scanDate.replace(' ', 'T'));
    }
    return new Date();
  };

  const [selectedDate, setSelectedDate] = useState(getLatestAvailableDate());
  const didSetDefaultDate = React.useRef(false);
  
  // Only set the default date on first load
  useEffect(() => {
    if (!didSetDefaultDate.current && availableDates.length) {
      setSelectedDate(getLatestAvailableDate());
      didSetDefaultDate.current = true;
    }
    // Do NOT reset selectedDate if it's already set!
  }, [availableDates, scans]);

  // For navigation, allow 1 year before and after today
  const minNavDate = subDays(new Date(), 365);
  const maxNavDate = addDays(new Date(), 365);

  // Handle date change
  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
    if (selectedAccount) {
      // Format selected date as YYYY-MM-DD (local time)
      const pad = n => n.toString().padStart(2, '0');
      const selectedDay = `${newDate.getFullYear()}-${pad(newDate.getMonth() + 1)}-${pad(newDate.getDate())}`;
      // Find the first datetime string from availableDates that matches the selected day
      const exactDateTime = availableDates.find(dt => dt.startsWith(selectedDay));
      if (exactDateTime) {
        dispatch(getComplianceList({ 
          accountNumber: selectedAccount,
          date: exactDateTime // send the full datetime string
        }));
      }
    }
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'request_initiated':
        return { bg: '#e3f2fd', text: '#1976d2' }; // Light blue
      case 'provisioning_resources':
        return { bg: '#fff3e0', text: '#f57c00' }; // Light orange
      case 'scan_in_progress':
        return { bg: '#e8f5e9', text: '#2e7d32' }; // Light green
      case 'generating_reports':
        return { bg: '#f3e5f5', text: '#7b1fa2' }; // Light purple
      case 'complete':
        return { bg: '#e8f5e9', text: '#2e7d32' }; // Light green
      case 'failed':
        return { bg: '#ffebee', text: '#c62828' }; // Light red
      default:
        return { bg: '#f5f5f5', text: '#757575' }; // Light grey
    }
  };

  // Helper function to format status text
  const formatStatusText = (status) => {
    if (!status) return '-';
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Helper function to title-case any string (e.g., for Run Type, Schedule Type)
  const toTitleCase = (str) => {
    if (!str) return '-';
    return str
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // DataGrid columns: scanDate, runType, runId, Status, Actions
  const columns = [
    {
      field: 'scanDate',
      headerName: 'Scan Date',
      width: 220,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        return params.value ? formatUtcDateSimple(params.value) : '-';
      },
    },
    {
      field: 'runType',
      headerName: 'Scan Type',
      width: 120,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => params.value ? toTitleCase(params.value) : '-',
    },
    {
      field: 'scheduleType',
      headerName: 'Schedule Type',
      width: 140,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => params.value ? toTitleCase(params.value) : '-',
    },
    {
      field: 'resourceScopeType',
      headerName: 'Scope',
      width: 120,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        if (!params.value) return '-';
        return params.value === 'Full Run' ? 'Full Scan' : params.value;
      },
    },
    {
      field: 'runStatus',
      headerName: 'Status',
      width: 160,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const status = params.value || params.row.runStatus;
        const colors = getStatusColor(status);
        return (
          <Typography
            sx={{
              color: colors.text,
              fontSize: '0.82rem',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              lineHeight: 1.3,
              letterSpacing: 0.1,
              justifyContent: 'center',
              m: 0,
            }}
          >
            {formatStatusText(status)}
          </Typography>
        );
      },
    },
    // {
    //   field: 'runId',
    //   headerName: 'Run ID',
    //   width: 180,
    //   headerAlign: 'center',
    //   align: 'center',
    //   renderCell: (params) => params.value || '-',
    // },
    {
      field: 'actions',
      headerName: '',
      width: 120,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            const scan = scans.find(s => s.scanDate === params.row.scanDate);
            if (scan) {
              onScanSelect(scan);
              onClose();
            }
          }}
          startIcon={<VisibilityIcon />}
          sx={{
            textTransform: 'none',
            borderColor: '#1976d2',
            color: '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: '#e3f2fd',
            },
          }}
        >
          View
        </Button>
      ),
    },
  ];

  // DataGrid rows: use scans prop with null checks
  const rows = scans.map((scan, idx) => ({
    id: scan.runId || idx,
    scanDate: scan.scanDate,
    runType: scan.runType || null,
    scheduleType: scan.scheduleType || null,
    resourceScopeType: scan.resourceScopeType || null,
    runStatus: scan.runStatus || null,
    runId: scan.runId || null,
  })).sort((a, b) => new Date(b.scanDate) - new Date(a.scanDate));

  console.log('Scans data:', scans);
  console.log('Compliance map:', complianceResultOrderedMap);
  console.log('Processed rows:', rows);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="scans-modal"
      aria-describedby="modal-for-viewing-scans"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '80%',
          maxWidth: 900,
          height: 'auto',
          bgcolor: 'background.paper',
          borderRadius: '10px',
          boxShadow: 24,
          p: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            backgroundColor: '#F5F5F5',
            padding: '20px 32px 16px 32px',
            borderTopLeftRadius: '10px',
            borderTopRightRadius: '10px',
            borderBottom: '1px solid #E0E0E0',
            gap: 0,
          }}
        >
          {/* Account */}
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 180, px: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
              Scans for Account:
            </Typography>
            <Typography variant="h6" fontWeight="bold" color="text.primary">
              {selectedAccount}
            </Typography>
          </Box>
          {/* Divider */}
          <Divider orientation="vertical" flexItem sx={{ mx: 3, my: 0, borderColor: '#E0E0E0' }} />
          {/* Date */}
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 180, px: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
              Date:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DatePicker
                selected={selectedDate}
                onChange={handleDateChange}
                minDate={minNavDate}
                maxDate={maxNavDate}
                filterDate={date =>
                  availableDates.some(d => {
                    const dt = new Date(d.replace(' ', 'T'));
                    return dt.toDateString() === date.toDateString();
                  })
                }
                dayClassName={date =>
                  availableDates.some(d => {
                    const dt = new Date(d.replace(' ', 'T'));
                    return dt.toDateString() === date.toDateString();
                  }) ? 'react-datepicker__day--has-scan-dot' : undefined
                }
                dateFormat="MMM d, yyyy"
                placeholderText="Select date"
                customInput={<DatePickerInput />}
                renderCustomHeader={({ date, decreaseMonth, increaseMonth }) => (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 8px 4px 8px' }}>
                    <button onClick={decreaseMonth} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer' }}>{'<'}</button>
                    <span style={{ fontWeight: 600, fontSize: 16 }}>
                      {date.toLocaleString('default', { month: 'long' })} {date.getFullYear()}
                    </span>
                    <button onClick={increaseMonth} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer' }}>{'>'}</button>
                    <button
                      style={{
                        marginLeft: 12,
                        padding: '2px 10px',
                        background: '#1976d2',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 4,
                        fontWeight: 500,
                        fontSize: 13,
                        cursor: 'pointer',
                        boxShadow: '0 1px 4px rgba(25, 118, 210, 0.08)'
                      }}
                      onClick={() => {
                        // Find today in availableDates
                        const today = new Date();
                        const todayStr = today.toISOString().split('T')[0];
                        const exactToday = availableDates.find(dt => dt.startsWith(todayStr));
                        if (exactToday) {
                          setSelectedDate(new Date(exactToday.replace(' ', 'T')));
                          handleDateChange(new Date(exactToday.replace(' ', 'T')));
                        }
                      }}
                      disabled={!availableDates.some(dt => {
                        const d = new Date(dt.replace(' ', 'T'));
                        return d.toDateString() === new Date().toDateString();
                      })}
                      title="Go to Today"
                    >
                      Today
                    </button>
                  </div>
                )}
              />
            </Box>
          </Box>
          {/* Divider */}
          {nextRunDate && (
            <Divider orientation="vertical" flexItem sx={{ mx: 3, my: 0, borderColor: '#E0E0E0' }} />
          )}
          {/* Next Run */}
          {nextRunDate && (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 180, px: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                Next Scan:
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="text.primary">
                {formatUtcDateSimple(nextRunDate)}
              </Typography>
            </Box>
          )}
          <IconButton onClick={onClose} sx={{ alignSelf: 'flex-start', ml: 'auto' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        {/* DataGrid */}
        <Box sx={{ p: 2, flex: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5, 10, 20, 100]}
            disableSelectionOnClick
            autoHeight
            sx={{
              '& .MuiDataGrid-cell': {
                cursor: 'default',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f5f5f5',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#F5F5F5',
                fontWeight: 'bold',
                fontSize: '14px',
                color: '#333',
              },
              '& .MuiDataGrid-cell': {
                fontSize: '14px',
                fontFamily: 'Arial, sans-serif',
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '1px solid #E0E0E0',
                backgroundColor: '#F5F5F5',
              },
            }}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default ListScans; 